import {
  Dashboard,
  DashboardCreate,
  DashboardDefaultUpdate,
  DashboardListResponse,
  DashboardUpdate,
  Dashlet,
  DashletCreate,
  DashletData,
  DashletGlobalFiltersSaveBulk,
  DashletReorderData,
  SyndicatedDashboard,
} from "types";

import API from "api/client";
import { envConfig } from "config";

const { dashboardsAPI } = envConfig;

export type GetAllDashboardsParams = {
  /**
   * `true`   - to limit the response to only dashboards with write access.
   *            Used when adding dashlets - where you must have edit permissions to add.
   * `false`  - Load all dashboards including those with read-only access
   */
  canWrite: boolean;
};

export type DeleteDashboardDashletArgs = {
  dashboardUuid: string;
  dashletUuid: string;
};

export type UpdateDashboardDashletsBulkParams = {
  dashboardUuid: string;
  dashlets: DashletReorderData[];
};

export type UpdateDashboardDashletsGlobalFiltersBulkParams = {
  dashboardUuid: string;
  filters: DashletGlobalFiltersSaveBulk;
};

/**
 * Returns dashboard by given uuid.
 */
const getDashboard = async (uuid: string): Promise<Dashboard> => {
  const { data } = await API.get<Dashboard>(`${dashboardsAPI}${uuid}`);
  return data;
};

/**
 * Update dashboard - currently only updating the dashboard name in @param payload
 */
const updateDashboard = async (uuid: string, payload: DashboardUpdate): Promise<Dashboard> => {
  const { data } = await API.put<Dashboard>(`${dashboardsAPI}${uuid}`, payload);
  return data;
};

/**
 * Update Dashboard Default by uuid
 */
const updateDashboardDefault = async (uuid: string, payload: DashboardDefaultUpdate): Promise<void> => {
  const { data } = await API.put<void>(`${dashboardsAPI}${uuid}/default`, payload);
  return data;
};

/**
 * Create a new dashboard with a given name
 */
const createDashboard = async (payload: DashboardCreate): Promise<Dashboard> => {
  const { data } = await API.post<Dashboard>(dashboardsAPI, payload);
  return data;
};

/**
 * Export dashboard to Microsoft Powerpoint.
 *
 */
const exportDashboard = async (uuid: string, payload: DashletData[]): Promise<string> => {
  const { data } = await API.post(`${dashboardsAPI}${uuid}/export`, payload);
  return data;
};

/**
 *
 * @param dashboardUuid
 */
const deleteDashboard = async (dashboardUuid: string): Promise<void> => {
  await API.delete(`${dashboardsAPI}${dashboardUuid}`);
};

/**
 * Clone dashboard - create an exact copy of an existing dashboard and its dashlets.
 */
const cloneDashboard = async (uuid: string, payload: DashboardCreate): Promise<Dashboard> => {
  const { data } = await API.post<Dashboard>(`${dashboardsAPI}${uuid}/clone`, payload);
  return data;
};

/**
 * Returns a list of syndicated dashboards which include enough information
 * to build a direct link to that dashboard.
 */
const getSyndicatedDashboards = async (): Promise<SyndicatedDashboard[]> => {
  const { data } = await API.get<SyndicatedDashboard[]>(`${dashboardsAPI}syndicated`);
  return data;
};

/**
 * Returns a list of dashboard list items associated to the current user.
 */
const getAllDashboards = async ({ canWrite }: GetAllDashboardsParams): Promise<DashboardListResponse> => {
  const { data } = await API.get<DashboardListResponse>(dashboardsAPI, { params: { canWrite } });
  return data;
};

/**
 * Returns a list of Dashlet instances associated to the provided dashboard @param dashboardId.
 */
const getDashlets = async (dashboardId: string): Promise<Dashlet[]> => {
  const { data } = await API.get<Dashlet[]>(`${dashboardsAPI}${dashboardId}/dashlets`);
  return data;
};

/**
 * Creates this dashlet as a clone.
 *
 * Note: Clone actually calls the create endpoint under the hood.
 * We decided to initially have a separate API function as the signatures are slightly different.
 * It may make sense to combine clone/create into one client side API function in the future,
 * but with only 1 dashlet type it is just kinda hard to tell right now.
 */
const cloneDashboardDashlet = async (dashlet: Dashlet): Promise<Dashlet> => {
  return createDashboardDashlet(dashlet);
};

/**
 * Creates the provided Dashlet.
 */
const createDashboardDashlet = async ({
  dashboardUuid,
  newDashboardName,
  column,
  ...dashlet
}: DashletCreate): Promise<Dashlet> => {
  const { data } = await API.post<Dashlet>(`${dashboardsAPI}dashlets`, {
    dashboardUuid,
    newDashboardName,
    dashlet,
    col: column,
  });
  return data;
};

const updateDashboardDashletsBulk = async ({
  dashboardUuid,
  dashlets,
}: UpdateDashboardDashletsBulkParams): Promise<Dashlet[]> => {
  /** Used for dashboard reorder with drag and drop, updating in a transaction */
  const { data } = await API.patch<Dashlet[]>(`${dashboardsAPI}${dashboardUuid}/dashlets`, { dashlets });
  return data;
};

/**
 * Edits the provided dashlet.
 */
const updateDashboardDashlet = async (dashlet: Dashlet): Promise<Dashlet> => {
  const { data } = await API.put<Dashlet>(
    `${dashboardsAPI}${dashlet.dashboardUuid}/dashlets/${dashlet.dashletUuid}`,
    dashlet,
  );
  return data;
};

const deleteDashboardDashlet = async ({ dashboardUuid, dashletUuid }: DeleteDashboardDashletArgs): Promise<void> => {
  await API.delete(`${dashboardsAPI}${dashboardUuid}/dashlets/${dashletUuid}`);
};

/**
 * Saves applied filters to all applicable dashlets in the provided dashboard.
 */
const updateDashboardDashletsGlobalFiltersBulk = async ({
  dashboardUuid,
  filters,
}: UpdateDashboardDashletsGlobalFiltersBulkParams): Promise<Dashlet[]> => {
  const { data } = await API.patch<Dashlet[]>(`${dashboardsAPI}${dashboardUuid}/dashlets/filters`, filters);
  return data;
};

export default {
  delete: deleteDashboard,
  get: getDashboard,
  clone: cloneDashboard,
  export: exportDashboard,
  getSyndicated: getSyndicatedDashboards,
  getAll: getAllDashboards,
  getDashlets,
  cloneDashboardDashlet,
  createDashboardDashlet,
  deleteDashboardDashlet,
  create: createDashboard,
  update: updateDashboard,
  updateDefault: updateDashboardDefault,
  updateDashboardDashlet,
  updateDashboardDashletsBulk,
  updateDashboardDashletsGlobalFiltersBulk,
};
